import React, { useState } from 'react';
import './Attendance.css';
import BatchViewer from '../Components/Viewers/BatchViewer';
import DataTable from './DataTable';
import { batchDetailsAPI, facultyAPI, funcAreaAPI, trngCourseAPI, trainingNeedsAPI, batchStatusAPI, emailAPI } from './api';
import { useAuth } from './auth';

export default function Attendance() {
  const { user } = useAuth();
  const [batchSrNo, setBatchSrNo] = useState('');
  const [showBatchViewer, setShowBatchViewer] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [batchInfo, setBatchInfo] = useState(null);
  const [facultyName, setFacultyName] = useState('');
  const [funcSubAreaName, setFuncSubAreaName] = useState('');
  const [trngCourseName, setTrngCourseName] = useState('');
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [checkAll, setCheckAll] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [isFreelanceBatch, setIsFreelanceBatch] = useState(false);
  const [attendedCount, setAttendedCount] = useState('');
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceMsg, setAttendanceMsg] = useState(null);

  const handleBatchSelect = (batch) => {
    setBatchSrNo(batch.batch_sr_no || '');
    setShowBatchViewer(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const srNo = String(batchSrNo).trim();
    if (!srNo) { alert('Please enter a Batch Sr.No.'); return; }

    setLoading(true);
    setBatchError('');
    setShowResults(false);
    setAttendanceSummary(null);
    setAttendanceMsg(null);
    try {
      const batchData = await batchDetailsAPI.getBatch(srNo);

      const status = (batchData?.trng_status || '').toUpperCase();

      // Always set batchInfo and fetch names regardless of status
      setBatchInfo({
        batch_sr_no: batchData.batch_sr_no || srNo,
        func_sub_area_code: batchData.func_sub_area_code || '',
        trng_course_code: batchData.trng_course_code || '',
        start_date: batchData.start_date || '',
        end_date: batchData.end_date || '',
        start_time: batchData.start_time || '',
        end_time: batchData.end_time || '',
        venue: batchData.venue || '',
        trng_location: batchData.trng_location || '',
        trng_city: batchData.trng_city || '',
        batch_size: batchData.batch_size || '',
        total_faculty_cost: batchData.total_faculty_cost || '',
        faculty_code: batchData.faculty_code || '',
        faculty_code_1: batchData.faculty_code_1 || '',
        faculty_code_2: batchData.faculty_code_2 || '',
        faculty_code_3: batchData.faculty_code_3 || '',
        faculty_code_4: batchData.faculty_code_4 || '',
        batch_type: batchData.batch_type || ''
      });

      const allFacultyCodes = [
        batchData.faculty_code, batchData.faculty_code_1, batchData.faculty_code_2,
        batchData.faculty_code_3, batchData.faculty_code_4,
      ].filter(Boolean);
      if (allFacultyCodes.length) {
        try {
          const names = await Promise.all(
            allFacultyCodes.map(fc =>
              facultyAPI.getAll({ faculty_code: fc })
                .then(fd => (Array.isArray(fd) && fd[0]?.faculty_name) ? fd[0].faculty_name : String(fc))
                .catch(() => String(fc))
            )
          );
          setFacultyName(names.join(', '));
        } catch { setFacultyName(allFacultyCodes.join(', ')); }
      } else { setFacultyName(''); }

      if (batchData?.func_sub_area_code) {
        try {
          const fa = await funcAreaAPI.getAll({ func_sub_area_code: batchData.func_sub_area_code });
          setFuncSubAreaName(Array.isArray(fa) && fa[0] ? fa[0].func_sub_area_name : batchData.func_sub_area_code);
        } catch { setFuncSubAreaName(batchData.func_sub_area_code); }
      } else { setFuncSubAreaName(''); }

      if (batchData?.trng_course_code) {
        try {
          const tc = await trngCourseAPI.getAll({ trng_course_code: batchData.trng_course_code });
          setTrngCourseName(Array.isArray(tc) && tc[0] ? tc[0].trng_course_name : batchData.trng_course_code);
        } catch { setTrngCourseName(batchData.trng_course_code); }
      } else { setTrngCourseName(''); }

      if (status === 'COMPLETED' || status === 'CANCELLED') {
        setBatchError(
          status === 'COMPLETED'
            ? 'You have already Marked this batch as Completed.'
            : 'You have already Marked this batch as Cancelled.'
        );
        setParticipants([]);
        setCheckedItems({});
        setCheckAll(false);
        setIsFreelanceBatch(false);
        setShowResults(true);
        return;
      }

      const isFreelance = (batchData.batch_type || '').toUpperCase() === 'FREE';
      setIsFreelanceBatch(isFreelance);
      setAttendedCount('');

      if (!isFreelance && status !== 'COMPLETED' && status !== 'CANCELLED') {
        const pData = await batchDetailsAPI.getParticipants(srNo);
        setParticipants(Array.isArray(pData) ? pData.filter((p) => p.attended_yn !== 'Y') : []);
        setCheckedItems({});
        setCheckAll(false);
      } else {
        setParticipants([]);
        setCheckedItems({});
        setCheckAll(false);
      }
      setShowResults(true);
    } catch (err) {
      alert('Failed to fetch batch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAll = (e) => {
    const checked = e.target.checked;
    setCheckAll(checked);
    const all = {};
    participants.forEach((_, i) => { all[i] = checked; });
    setCheckedItems(all);
  };

  const handleItemCheck = (index) => {
    const updated = { ...checkedItems, [index]: !checkedItems[index] };
    setCheckedItems(updated);
    setCheckAll(participants.every((_, i) => updated[i]));
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '';

  const handleSaveAttendance = async () => {
    const attended = participants.filter((_, i) => checkedItems[i]);
    const identifiedBy = user?.sal_code != null ? String(user.sal_code) : null;
    if (!attended.length) { alert('No participants selected.'); return; }
    try {
      console.log('[Attendance] Saving attendance for', attended.length, 'participants');
      const updateResults = await Promise.allSettled(
        attended.map(async (p) => {
          console.log(`[Attendance] Updating sal_code=${p.sal_code} trng_course_code=${p.trng_course_code} date_of_entry=${p.date_of_entry}`);
          const res = await trainingNeedsAPI.update(p.sal_code, p.trng_course_code, p.date_of_entry, {
            attended_yn: 'Y',
            identified_by: identifiedBy,
            updated_by: identifiedBy,
            batch_sr_no: batchSrNo
          });
          const json = await res.json().catch(() => ({}));
          console.log(`[Attendance] Update result for sal_code=${p.sal_code}:`, res.status, json);
          if (res.status !== 200) throw new Error(`Update failed for ${p.sal_code}: ${res.status}`);
          return { sal_code: p.sal_code, status: res.status, json };
        })
      );
      const failed = updateResults.filter(r => r.status === 'rejected');
      if (failed.length) {
        console.error('[Attendance] Failed updates:', failed);
        alert(`Failed to save attendance for ${failed.length} participant(s). Please try again.`);
        return;
      }
      console.log('[Attendance] All updates succeeded, marking batch COMPLETED');
      await batchStatusAPI.update(batchSrNo, { trng_status: 'COMPLETED', branch: batchInfo?.branch });

      const startDate = fmtDate(batchInfo?.start_date);
      const endDate = fmtDate(batchInfo?.end_date);
      const batchDetailsHtml = `
        Batch No. : <B>${batchSrNo}</B><br>
        Name of the program: <B>${funcSubAreaName}</B> : ${trngCourseName}<br>
        Date: From <b>${startDate}</b> To <b>${endDate}</b><br>
        Time: From <B>${batchInfo?.start_time || ''}</B> to <B>${batchInfo?.end_time || ''}</B><br>
        Venue : <b>${[batchInfo?.venue, batchInfo?.trng_location, batchInfo?.trng_city].filter(Boolean).join(', ')}</b><br>
        Batch size: <B>${batchInfo?.batch_size || ''}</B><br>`;

      const empRows = attended.map(p => `<tr><td>${p.sal_code}</td><td>${p.emp_name || ''}</td></tr>`).join('');

      try {
        const emailPromises = [];

        // Group 1 — Attended employees
        attended.forEach((p) => {
          const empEmail = p.emp_email || '';
          if (empEmail.includes('@')) {
            emailPromises.push(
              emailAPI.send({
                recipient: empEmail,
                subject: `Attendance Confirmation — ${trngCourseName} (Batch #${batchSrNo})`,
                body: `Dear ${p.emp_name || p.sal_code},<br><br>
                  Your attendance for the following training program has been recorded.<br><br>
                  ${batchDetailsHtml}
                  Faculty: <B>${facultyName || ''}</B><br><br>
                  Thank you for attending.`
              }).catch(() => {})
            );
          } else {
            // skip invalid email
          }
        });

        // Group 2 — Superior (L+1): one email per unique superior email with all their employees listed
        const uniqueSuperiors = {};
        attended.forEach((p) => {
          if (p.sup_email) {
            if (!uniqueSuperiors[p.sup_email])
              uniqueSuperiors[p.sup_email] = { name: p.sup_name || '', empNames: [] };
            uniqueSuperiors[p.sup_email].empNames.push(p.emp_name || String(p.sal_code));
          }
        });
        Object.entries(uniqueSuperiors).forEach(([email, sup]) => {
          emailPromises.push(
            emailAPI.notifySuperior({
              superiorData: { emailId: email, name: sup.name },
              employeeNames: sup.empNames.join(', '),
              trainingData: {
                batchSrNo,
                funcSubAreaName,
                trngCourseName,
                startDate,
                endDate,
                startTime: batchInfo?.start_time || '',
                endTime: batchInfo?.end_time || '',
                venue: batchInfo?.venue || '',
                trngLocation: batchInfo?.trng_location || '',
                trngCity: batchInfo?.trng_city || '',
                batchSize: batchInfo?.batch_size || '',
                faculty: facultyName || ''
              }
            }).catch(() => {})
          );
        });

        // Group 3 — Regional Service Manager: unique by email
        const uniqueRSM = {};
        attended.forEach((p) => {
          if (p.regional_service_manager_email && !uniqueRSM[p.regional_service_manager_email])
            uniqueRSM[p.regional_service_manager_email] = true;
        });
        Object.keys(uniqueRSM).forEach((email) => {
          emailPromises.push(
            emailAPI.send({
              recipient: email,
              subject: `Attendance Details — ${trngCourseName} (Batch #${batchSrNo})`,
              body: `Dear Sir,<br><br>
                The following training program has been completed successfully.<br><br>
                ${batchDetailsHtml}
                <br>Attended Employees:<br>
                <Table border=1 Cellspacing=0 Cellpadding=3>
                  <tr><TD><B>Sal Code</B></TD><TD><B>Name</B></TD></tr>
                  ${empRows}
                </Table><br>Thanks &amp; regards`
            }).catch(() => {})
          );
        });

        // Group 4 — Branch Service Incharge: unique by email
        const uniqueBSI = {};
        attended.forEach((p) => {
          if (p.branch_service_incharge_email && !uniqueBSI[p.branch_service_incharge_email])
            uniqueBSI[p.branch_service_incharge_email] = true;
        });
        Object.keys(uniqueBSI).forEach((email) => {
          emailPromises.push(
            emailAPI.send({
              recipient: email,
              subject: `Attendance Details — ${trngCourseName} (Batch #${batchSrNo})`,
              body: `Dear Sir,<br><br>
                The following training program has been completed successfully.<br><br>
                ${batchDetailsHtml}
                <br>Attended Employees:<br>
                <Table border=1 Cellspacing=0 Cellpadding=3>
                  <tr><TD><B>Sal Code</B></TD><TD><B>Name</B></TD></tr>
                  ${empRows}
                </Table><br>Thanks &amp; regards`
            }).catch(() => {})
          );
        });

        // Group 5 — Training MIS (hardcoded)
        emailPromises.push(
          emailAPI.attendanceAccounts({
            accountsEmail: 'gaikwads@godrej.com',
            trainingData: {
              batchSrNo,
              funcSubAreaName,
              trngCourseName,
              startDate,
              endDate,
            },
            attendedEmployees: attended.map(p => ({ salCode: p.sal_code, name: p.emp_name || '' })),
            faculty: facultyName || ''
          }).catch(() => {})
        );

        // Group 6 — DPH (hardcoded until dph_email is integrated)
        emailPromises.push(
          emailAPI.attendanceDPH({
            dphEmail: 'gaikwads@godrej.com',
            dphName: 'DPH',
            dphDivision: '',
            trainingData: {
              batchSrNo,
              funcSubAreaName,
              trngCourseName,
              startDate,
              endDate,
              startTime: batchInfo?.start_time || '',
              endTime: batchInfo?.end_time || '',
              venue: batchInfo?.venue || '',
            },
            attendedEmployees: attended.map(p => ({ salCode: p.sal_code, name: p.emp_name || '', bcRcCc: p.bc_rc_cc || '', division: p.division || '' })),
          }).catch(() => {})
        );

        await Promise.all(emailPromises);

        // Group 6 — Faculty: sequential, using fac.email fallback
        const facultyCodes = [
          batchInfo?.faculty_code, batchInfo?.faculty_code_1, batchInfo?.faculty_code_2,
          batchInfo?.faculty_code_3, batchInfo?.faculty_code_4,
        ].filter(Boolean);
        const facultyList = [];
        for (const fc of facultyCodes) {
          try {
            const fd = await facultyAPI.getAll({ faculty_code: fc });
            const fac = Array.isArray(fd) ? fd[0] : null;
            if (!fac) continue;
            const facEmail = fac?.faculty_email || fac?.email || '';
            if (!facEmail.includes('@')) continue;
            await emailAPI.send({
              recipient: facEmail,
              subject: `Attendance Details — ${trngCourseName} (Batch #${batchSrNo})`,
              body: `Dear ${fac.faculty_name || ''},<br><br>
                The following training program has been completed successfully.<br><br>
                ${batchDetailsHtml}
                <br>Attended Employees:<br>
                <Table border=1 Cellspacing=0 Cellpadding=3>
                  <tr><TD><B>Sal Code</B></TD><TD><B>Name</B></TD></tr>
                  ${empRows}
                </Table><br>Thanks &amp; regards`
            }).catch(() => {});
            facultyList.push({ name: fac.faculty_name || '', email: facEmail });
          } catch { /* skip */ }
        }
      } catch { /* skip email errors */ }

      // Build summary
      const smBsiList = [];
      const seenRSMEmails = new Set();
      const seenBSIEmails = new Set();
      attended.forEach((p) => {
        if (p.regional_service_manager_email && !seenRSMEmails.has(p.regional_service_manager_email)) {
          seenRSMEmails.add(p.regional_service_manager_email);
          smBsiList.push({ name: p.regional_service_manager_name || '', email: p.regional_service_manager_email });
        }
        if (p.branch_service_incharge_email && !seenBSIEmails.has(p.branch_service_incharge_email)) {
          seenBSIEmails.add(p.branch_service_incharge_email);
          smBsiList.push({ name: p.branch_service_incharge_name || '', email: p.branch_service_incharge_email });
        }
      });

      // Re-fetch faculty list for summary (facultyList may be out of scope from inner try)
      const summaryFacultyList = [];
      const summaryFacultyCodes = [
        batchInfo?.faculty_code, batchInfo?.faculty_code_1, batchInfo?.faculty_code_2,
        batchInfo?.faculty_code_3, batchInfo?.faculty_code_4,
      ].filter(Boolean);
      for (const fc of summaryFacultyCodes) {
        try {
          const fd = await facultyAPI.getAll({ faculty_code: fc });
          const fac = Array.isArray(fd) ? fd[0] : null;
          const fe = fac?.faculty_email || fac?.email || '';
          if (fe) summaryFacultyList.push({ name: fac.faculty_name || '', email: fe });
        } catch { /* skip */ }
      }

      const superiorList = [];
      const seenSupEmails = new Set();
      attended.forEach((p) => {
        if (p.sup_email && !seenSupEmails.has(p.sup_email)) {
          seenSupEmails.add(p.sup_email);
          superiorList.push({ email: p.sup_email, name: p.sup_name || '' });
        }
      });

      setAttendanceMsg({ success: true, text: `Attendance saved for ${attended.length} participant(s) — Batch #${batchSrNo}.` });
      setAttendanceSummary({
        employees: attended.map(p => p.emp_name || String(p.sal_code)),
        smBsiList,
        superiorList,
        facultyList: summaryFacultyList,
        accountsEmail: 'gaikwads@godrej.com',
        dphEmail: 'gaikwadis@godrej.com',
      });
      setParticipants([]);
      setCheckedItems({});
      setCheckAll(false);
    } catch {
      alert('Failed to save attendance.');
    }
  };

  const handleMarkCompleted = async () => {
    if (!attendedCount || isNaN(Number(attendedCount))) {
      alert('Please enter a valid Attended Count.');
      return;
    }
    try {
      await batchStatusAPI.update(batchSrNo, {
        trng_status: 'COMPLETED',
        branch: batchInfo?.branch,
        number_attended: Number(attendedCount)
      });

      // Notify faculty for Freelance batch completion
      const freelanceFacultyCodes = [
        batchInfo?.faculty_code, batchInfo?.faculty_code_1, batchInfo?.faculty_code_2,
        batchInfo?.faculty_code_3, batchInfo?.faculty_code_4,
      ].filter(Boolean);
      const flStartDate = fmtDate(batchInfo?.start_date);
      const flEndDate = fmtDate(batchInfo?.end_date);
      const flBatchDetailsHtml = `
        Batch No. : <B>${batchSrNo}</B><br>
        Name of the program: <B>${funcSubAreaName}</B> : ${trngCourseName}<br>
        Date: From <b>${flStartDate}</b> To <b>${flEndDate}</b><br>
        Time: From <B>${batchInfo?.start_time || ''}</B> to <B>${batchInfo?.end_time || ''}</B><br>
        Venue : <b>${[batchInfo?.venue, batchInfo?.trng_location, batchInfo?.trng_city].filter(Boolean).join(', ')}</b><br>
        Batch size: <B>${batchInfo?.batch_size || ''}</B><br>`;
      for (const fc of freelanceFacultyCodes) {
        try {
          const fd = await facultyAPI.getAll({ faculty_code: fc });
          const fac = Array.isArray(fd) ? fd[0] : null;
          const facEmail = fac?.faculty_email || fac?.email || '';
          if (facEmail.includes('@')) {
            emailAPI.send({
              recipient: facEmail,
              subject: `Attendance Details — ${trngCourseName} (Batch #${batchSrNo})`,
              body: `Dear ${fac.faculty_name || ''},<br><br>
                The following Freelance training batch has been marked as completed.<br><br>
                ${flBatchDetailsHtml}
                Total employees attended: <B>${attendedCount}</B><br><br>Thanks &amp; regards`
            }).catch(() => {});
          }
        } catch { /* skip */ }
      }

      alert('Batch marked as Completed successfully!');
      setShowResults(false);
      setBatchSrNo('');
      setAttendedCount('');
    } catch {
      alert('Failed to mark batch as Completed.');
    }
  };

  const numDays = batchInfo?.start_date && batchInfo?.end_date
    ? Math.floor((new Date(batchInfo.end_date) - new Date(batchInfo.start_date)) / (1000 * 60 * 60 * 24)) + 1
    : '';

  return (
    <div className="att-wrapper">
      {showBatchViewer && (
        <div className="att-modal-overlay">
          <div className="att-modal-box">
            <div className="att-modal-header">
              <h3>Select Batch</h3>
              <button onClick={() => setShowBatchViewer(false)} className="att-modal-close">×</button>
            </div>
            <BatchViewer selectable onSelect={handleBatchSelect} autoLoad filterStatus="SCHEDULED" loadTrigger={showBatchViewer} />
          </div>
        </div>
      )}

      <form className="att-form" onSubmit={handleSubmit}>
        <div className="att-form-row">
          <label className="att-label">Batch Sr.No.</label>
          <input
            type="text"
            className="att-input"
            value={batchSrNo}
            onChange={(e) => setBatchSrNo(e.target.value)}
          />
          <button type="button" className="att-btn" onClick={() => setShowBatchViewer(true)}>Get</button>
        </div>
        <div className="att-form-row att-center">
          <button type="submit" className="att-btn att-btn-outline" disabled={loading}>
            {loading ? 'Loading...' : 'Submit'}
          </button>
        </div>
      </form>

      {showResults && (
        <div className="att-results">
          <div className="att-section-title">Training Batch Details</div>
          <table className="att-details-table">
            <tbody>
              <tr>
                <td className="att-td-label">Functional Area</td>
                <td>{funcSubAreaName}</td>
                <td className="att-td-label">Training Course Name</td>
                <td>{trngCourseName} [{batchInfo?.trng_course_code}]</td>
              </tr>
              <tr>
                <td className="att-td-label">Start Date</td>
                <td>{fmtDate(batchInfo?.start_date)}</td>
                <td className="att-td-label">End Date</td>
                <td>{fmtDate(batchInfo?.end_date)}</td>
              </tr>
              <tr>
                <td className="att-td-label">Start Time</td>
                <td>{batchInfo?.start_time}</td>
                <td className="att-td-label">End Time</td>
                <td>{batchInfo?.end_time}</td>
              </tr>
              <tr>
                <td className="att-td-label">No. of Days</td>
                <td>{numDays}</td>
                <td className="att-td-label">Faculty</td>
                <td>{facultyName}</td>
              </tr>
              <tr>
                <td className="att-td-label">Batch Size</td>
                <td>{batchInfo?.batch_size}</td>
                <td className="att-td-label">Venue, Location, City</td>
                <td>{[batchInfo?.venue, batchInfo?.trng_location, batchInfo?.trng_city].filter(Boolean).join(', ')}</td>
              </tr>
              <tr>
                <td className="att-td-label">Total Faculty Cost</td>
                <td>{batchInfo?.total_faculty_cost}</td>
                <td></td><td></td>
              </tr>
            </tbody>
          </table>

          {attendanceSummary ? (
            <div style={{ fontFamily: 'GEG-Body, sans-serif', fontSize: '13px', maxWidth: '600px', margin: '0 auto', lineHeight: '1.8' }}>
              {attendanceMsg && (
                <div style={{
                  padding: '8px 16px', borderRadius: '4px', marginBottom: '16px',
                  backgroundColor: attendanceMsg.success ? '#e6f4ea' : '#fdecea',
                  border: `1px solid ${attendanceMsg.success ? '#34a853' : '#d93025'}`,
                  color: attendanceMsg.success ? '#1e7e34' : '#c0392b',
                  fontSize: '12.5px', textAlign: 'center'
                }}>
                  {attendanceMsg.text}
                </div>
              )}
              <div style={{ marginBottom: '12px' }}>
                {attendanceSummary.employees.map((name, i) => (
                  <div key={i} style={{ fontWeight: 'bold', textTransform: 'uppercase', color: 'red' }}>{name}</div>
                ))}
              </div>
              {attendanceSummary.smBsiList.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ marginBottom: '4px', color: 'blue' }}>Mail has been sent to following Service Managers and Branch Service Incharge</div>
                  {attendanceSummary.smBsiList.map((m, i) => (
                    <div key={i} style={{ color: 'blue' }}><span style={{ color: 'red' }}>{m.name}</span> - {m.email}</div>
                  ))}
                </div>
              )}
              {attendanceSummary.superiorList?.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ marginBottom: '4px', color: 'blue' }}>Mail has been sent to following L+1</div>
                  {attendanceSummary.superiorList.map((s, i) => (
                    <div key={i} style={{ color: 'blue' }}><span style={{ color: 'red' }}>{s.name || s.email}</span> - {s.email}</div>
                  ))}
                </div>
              )}
              {attendanceSummary.facultyList.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ marginBottom: '4px', color: 'blue' }}>Mail has been sent to following Faculty</div>
                  {attendanceSummary.facultyList.map((f, i) => (
                    <div key={i} style={{ color: 'blue' }}><span style={{ color: 'red' }}>{f.name}</span> ({f.email})</div>
                  ))}
                </div>
              )}
              {/* <div style={{ marginBottom: '12px' }}>
                <div style={{ marginBottom: '4px', color: 'blue' }}>Mail has been sent to Training MIS (Accounts)</div>
                <div style={{ color: 'blue' }}><span style={{ color: 'red' }}>{attendanceSummary.accountsEmail}</span></div>
              </div> */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ marginBottom: '4px', color: 'blue' }}>Training Batch Details has been sent to DPH</div>
                <div style={{ color: 'blue' }}><span style={{ color: 'red' }}>{attendanceSummary.dphEmail}</span></div>
              </div>
            </div>
          ) : batchError ? (
            <p style={{ color: 'red', fontFamily: 'GEG-Body, sans-serif', fontSize: '13px', textAlign: 'center', margin: '16px 0' }}>
              {batchError}
            </p>
          ) : isFreelanceBatch ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', margin: '20px 0' }}>
              <div className="att-form-row">
                <label className="att-label">Attended Count</label>
                <input
                  type="number"
                  className="att-input"
                  value={attendedCount}
                  onChange={(e) => setAttendedCount(e.target.value)}
                  min="0"
                />
              </div>
              <button type="button" className="att-btn att-btn-outline" onClick={handleMarkCompleted}>
                Mark as Completed
              </button>
            </div>
          ) : (
            <>
              <div className="att-section-title">Participants</div>
              <DataTable
                columns={[
                  { header: 'Sr.No.', renderCell: (_v, _r, i) => i + 1 },
                  { header: 'Name', accessor: (r) => r.emp_name || '' },
                  { header: 'Salary Code', accessor: (r) => r.sal_code || '' },
                  { header: 'Location', accessor: (r) => r.locn || '' },
                  { header: 'Band', accessor: (r) => r.band || '' },
                  { header: 'Division', accessor: (r) => r.division || '' },
                  { header: 'Function', accessor: (r) => r.func || '' },
                  {
                    header: <span>Attended<br /><input type="checkbox" checked={checkAll} onChange={handleCheckAll} /> Check All</span>,
                    className: 'att-center',
                    renderCell: (_v, _r, i) => (
                      <input
                        type="checkbox"
                        checked={checkedItems[i] || false}
                        onChange={() => handleItemCheck(i)}
                      />
                    )
                  }
                ]}
                data={participants}
                emptyMessage="No participants found"
                rowKey={(r, i) => r.sal_code || i}
              />
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                <button type="button" className="att-btn" onClick={handleSaveAttendance}>
                  Save Attendance
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
